/**
 * LeadDetailModal - View and edit lead details
 */

import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { EmailComposeModal } from "@/components/email/EmailComposeModal";
import { EmailHistorySection } from "@/components/email/EmailHistorySection";
import type { Lead, LeadStatus, LeadNote, OutreachNote } from "../Lead.types";
import { LeadStatusBadge } from "./LeadStatusBadge";
import { LeadTagPicker } from "./LeadTagPicker";
import { LeadTagChips } from "./LeadTagChips";
import {
  useLead,
  useUpdateLead,
  useLogContact,
  useUpdateLeadStatus,
  useLeadNotes,
  useAddLeadNote,
  useDeleteLeadNote,
  useLeadTags,
} from "../hooks/useLeads";

interface LeadDetailModalProps {
  leadId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onConvert?: (leadId: string) => void;
}

export function LeadDetailModal({
  leadId,
  isOpen,
  onClose,
  onConvert,
}: LeadDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Lead>>({});
  const [contactNote, setContactNote] = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [showContactForm, setShowContactForm] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [newNoteStatus, setNewNoteStatus] = useState("");
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [emailHistoryRefreshKey, setEmailHistoryRefreshKey] = useState(0);

  const { data: lead, isLoading } = useLead(leadId);
  const { data: notes = [], isLoading: notesLoading } = useLeadNotes(leadId);
  const { data: tagCatalog = [] } = useLeadTags();
  const updateMutation = useUpdateLead();
  const logContactMutation = useLogContact();
  const updateStatusMutation = useUpdateLeadStatus();
  const addNoteMutation = useAddLeadNote();
  const deleteNoteMutation = useDeleteLeadNote();

  useEffect(() => {
    if (lead) {
      setEditData(lead);
    }
  }, [lead]);

  const handleSave = async () => {
    if (!leadId) return;
    await updateMutation.mutateAsync({ leadId, data: editData });
    setIsEditing(false);
  };

  const handleLogContact = async () => {
    if (!leadId || !contactNote.trim()) return;
    await logContactMutation.mutateAsync({
      leadId,
      data: { note: contactNote, method: contactMethod || undefined },
    });
    setContactNote("");
    setContactMethod("");
    setShowContactForm(false);
  };

  const handleStatusChange = async (status: LeadStatus) => {
    if (!leadId) return;
    await updateStatusMutation.mutateAsync({ leadId, status });
  };

  const handleAddNote = async () => {
    if (!leadId || !newNoteContent.trim()) return;
    await addNoteMutation.mutateAsync({
      leadId,
      content: newNoteContent,
      status: newNoteStatus || undefined,
    });
    setNewNoteContent("");
    setNewNoteStatus("");
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!leadId) return;
    await deleteNoteMutation.mutateAsync({ leadId, noteId });
  };

  const canConvert = lead?.status === "QUALIFIED";

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return (
  <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={lead?.name || "Lead Details"}
      size="lg"
    >
      {isLoading ? (
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : lead ? (
        <div className="divide-y divide-gray-100">
          {/* Status and actions */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <LeadStatusBadge
                status={lead.status}
                onStatusChange={handleStatusChange}
                transitionsOnly
              />
            </div>

            <div className="flex items-center gap-2">
              {canConvert && onConvert && (
                <button
                  onClick={() => onConvert(lead.id)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  Convert to Client
                </button>
              )}
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                {isEditing ? "Cancel" : "Edit"}
              </button>
            </div>
          </div>

          {/* Details */}
          <div className="p-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.name || ""}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">{lead.name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sector</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.sector || ""}
                  onChange={(e) => setEditData({ ...editData, sector: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">{lead.sector || "-"}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={editData.emailAddress || ""}
                  onChange={(e) => setEditData({ ...editData, emailAddress: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <p
                    className={`text-sm ${
                      lead.emailInvalid ? "line-through text-red-500" : "text-gray-900"
                    }`}
                  >
                    {lead.emailAddress || "-"}
                  </p>
                  {lead.emailInvalid && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-red-100 text-red-700"
                      title={lead.emailInvalidReason || "Email hard-bounced"}
                    >
                      Bounced
                    </span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editData.phone || ""}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">{lead.phone || "-"}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Website</label>
              {isEditing ? (
                <input
                  type="url"
                  value={editData.website || ""}
                  onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : lead.website ? (
                <a
                  href={lead.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  {lead.website}
                </a>
              ) : (
                <p className="text-sm text-gray-900">-</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Instagram</label>
              {isEditing ? (
                <input
                  type="url"
                  value={editData.instagramUrl || ""}
                  onChange={(e) => setEditData({ ...editData, instagramUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : lead.instagramUrl ? (
                <div className="flex items-center gap-2">
                  <a
                    href={lead.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Instagram
                  </a>
                  {lead.instagramFollowers && (
                    <span className="text-xs text-gray-500">
                      ({lead.instagramFollowers.toLocaleString()} followers)
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-900">-</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.address || ""}
                  onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">{lead.address || "-"}</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              {isEditing ? (
                <textarea
                  value={editData.description || ""}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : (
                <p className="text-sm text-gray-900">{lead.description || "-"}</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Tags</label>
              {isEditing ? (
                <LeadTagPicker
                  selectedTags={editData.tags || []}
                  catalog={tagCatalog}
                  onChange={(tags) => setEditData({ ...editData, tags })}
                  disabled={updateMutation.isPending}
                />
              ) : lead.tags && lead.tags.length > 0 ? (
                <LeadTagChips tags={lead.tags} catalog={tagCatalog} limit={20} />
              ) : (
                <p className="text-sm text-gray-900">-</p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="p-4 flex justify-end gap-2">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}

          {/* Outreach Timeline */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">
                Outreach Timeline ({lead.contactAttempts} contacts)
              </h3>
              <div className="flex items-center gap-2">
                {lead.emailAddress && (
                  <button
                    onClick={() => setShowEmailCompose(true)}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    Send Email
                  </button>
                )}
                <button
                  onClick={() => setShowContactForm(!showContactForm)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  {showContactForm ? "Cancel" : "+ Log Contact"}
                </button>
              </div>
            </div>

            {showContactForm && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <textarea
                  value={contactNote}
                  onChange={(e) => setContactNote(e.target.value)}
                  placeholder="What happened in this interaction?"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
                />
                <div className="flex items-center gap-2">
                  <select
                    value={contactMethod}
                    onChange={(e) => setContactMethod(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
                  >
                    <option value="">Method (optional)</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="instagram">Instagram</option>
                    <option value="in-person">In Person</option>
                    <option value="other">Other</option>
                  </select>
                  <button
                    onClick={handleLogContact}
                    disabled={!contactNote.trim() || logContactMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {logContactMutation.isPending ? "Saving..." : "Log"}
                  </button>
                </div>
              </div>
            )}

            {lead.outreachNotes.length === 0 ? (
              <p className="text-sm text-gray-500">No outreach activity yet</p>
            ) : (
              <div className="space-y-3">
                {lead.outreachNotes.map((note: OutreachNote, idx: number) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-400 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{note.note}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{formatDate(note.contactedAt)}</span>
                        {note.method && (
                          <>
                            <span>-</span>
                            <span className="capitalize">{note.method}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Email History */}
          <div className="p-4 border-t">
            <EmailHistorySection
              leadId={lead.id}
              refreshKey={emailHistoryRefreshKey}
            />
          </div>

          {/* Notes Section */}
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Notes ({notes.length})
            </h3>

            {/* Add note form */}
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Add a note about this lead..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
              />
              <div className="flex items-center gap-2">
                <select
                  value={newNoteStatus}
                  onChange={(e) => setNewNoteStatus(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
                >
                  <option value="">Status (optional)</option>
                  <option value="called">Called</option>
                  <option value="emailed">Emailed</option>
                  <option value="dm_sent">DM Sent</option>
                  <option value="researched">Researched</option>
                  <option value="visited">Visited</option>
                  <option value="other">Other</option>
                </select>
                <button
                  onClick={handleAddNote}
                  disabled={!newNoteContent.trim() || addNoteMutation.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {addNoteMutation.isPending ? "Adding..." : "Add Note"}
                </button>
              </div>
            </div>

            {/* Notes list */}
            {notesLoading ? (
              <div className="text-sm text-gray-500">Loading notes...</div>
            ) : notes.length === 0 ? (
              <p className="text-sm text-gray-500">No notes yet</p>
            ) : (
              <div className="space-y-3">
                {notes.map((note: LeadNote) => (
                  <div key={note.id} className="flex gap-3 group">
                    <div className="w-2 h-2 mt-2 rounded-full bg-purple-400 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{note.content}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{formatDate(note.createdAt)}</span>
                        {note.status && (
                          <>
                            <span>-</span>
                            <span className="capitalize bg-gray-100 px-1.5 py-0.5 rounded-sm">
                              {note.status.replace("_", " ")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      disabled={deleteNoteMutation.isPending}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 transition-opacity"
                      title="Delete note"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="p-4 text-xs text-gray-500">
            <p>Created: {formatDate(lead.createdAt)}</p>
            {lead.updatedAt && <p>Updated: {formatDate(lead.updatedAt)}</p>}
            {lead.score !== undefined && lead.score !== null && (
              <p>Score: {lead.score.toFixed(0)}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="p-6 text-center text-gray-500">Lead not found</div>
      )}
    </Modal>

    {lead && (
      <EmailComposeModal
        isOpen={showEmailCompose}
        onClose={() => setShowEmailCompose(false)}
        recipientEmail={lead.emailAddress || ""}
        recipientName={lead.name}
        leadId={lead.id}
        initialBody={lead.outreachEmailData?.email || ""}
        onEmailSent={() => {
          setEmailHistoryRefreshKey((k) => k + 1);
        }}
      />
    )}
  </>
  );
}
